import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import CashApi from '../services/CashApi';
import noop from '../utils/noop';
import SnackbarContext from './SnackbarContext';
import useVenmoCashFundFlag from '../hooks/useVenmoCashFundFlag';
import ExperimentsContext from './ExperimentsContext';

const Context = createContext({
  error: null,
  venmoAccountHandle: null,
  upsertVenmoAccount: noop,
  isVenmoEnabled: false,
});

export default Context;

export const VenmoAccountContextProvider = ({children}) => {
  const {showSnackbar, dismissSnackbar} = useContext(SnackbarContext);
  const isVenmoCashFundFeatureFlagEnabled = useVenmoCashFundFlag();

  // State
  const [errorState, setErrorState] = useState(false);
  const [venmoAccountHandle, setVenmoAccountHandle] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const {
    experiments: {venmoIntegration = {}},
  } = useContext(ExperimentsContext);

  const {isExperimentLoading = true, isInControl = true} = venmoIntegration;

  const isVenmoEnabled =
    !!venmoAccountHandle ||
    (isVenmoCashFundFeatureFlagEnabled && !isExperimentLoading && !isInControl);

  const handleError = useCallback(
    (error, id, message) => {
      Honeybadger.notify(error, id);

      dismissSnackbar();
      showSnackbar(message);
      setErrorState(error);
    },
    [showSnackbar, dismissSnackbar],
  );

  const fetchVenmoAccount = useCallback(async () => {
    try {
      const response = await CashApi.getPaymentAccounts();

      if (response.ok) {
        const accounts = await response.json();
        setVenmoAccountHandle(accounts?.venmo?.venmoHandle ?? null);
      } else if (response.status === 404) {
        setVenmoAccountHandle(null);
      } else {
        throw Error(response.statusText);
      }
    } catch (error) {
      handleError(
        error,
        'FetchVenmoAccountError',
        'Failed to fetch Venmo account. Please try again.',
      );
    } finally {
      setHasFetched(true);
    }
  }, [handleError]);

  const upsertVenmoAccount = useCallback(
    async venmoHandle => {
      const sanitizedVenmoHandle = venmoHandle.replace(/^@/, '');
      try {
        if (!isVenmoEnabled) {
          throw Error('Venmo Payment Method is not enabled');
        }

        const response = await CashApi.createVenmoAccount({
          venmoHandle: sanitizedVenmoHandle,
        });
        if (response.ok) {
          setVenmoAccountHandle(sanitizedVenmoHandle);
          dismissSnackbar();
          showSnackbar(
            `Venmo account updated successfully to @${sanitizedVenmoHandle}`,
          );
          return {
            success: true,
            message: 'Venmo account updated successfully.',
          };
        }

        if (response.status === 422) {
          throw new Error('Enter a valid Venmo username.');
        }

        throw Error(response.statusText);
      } catch (error) {
        handleError(
          error,
          'UpdateVenmoAccountError',
          'Failed to update Venmo account. Please try again.',
        );
        return {
          success: false,
          message: error.message,
        };
      }
    },
    [handleError, showSnackbar, dismissSnackbar, isVenmoEnabled],
  );

  // Effects
  useEffect(() => {
    if (!hasFetched) {
      fetchVenmoAccount();
    }
  }, [hasFetched, fetchVenmoAccount]);

  const values = useMemo(
    () => ({
      venmoAccountHandle,
      error: errorState,
      upsertVenmoAccount,
      isVenmoEnabled,
    }),
    [venmoAccountHandle, errorState, upsertVenmoAccount, isVenmoEnabled],
  );

  return <Context.Provider value={values}>{children}</Context.Provider>;
};

VenmoAccountContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
